/**
 * `/join/$code`, where a shared pool link resolves.
 *
 * The URL shape comes from `POOL_JOIN_URL_BASE`
 * (`services/pool-api/deploy/k8s_config.sh:30`) plus `buildJoinUrl`
 * (`services/pool-api/src/lib/joinCode.ts`): every share link this platform
 * hands out is `https://app.sportsmet.com/join/<CODE>`.
 *
 * This route forwards to the already-shipped ISSUE-480 join dialog
 * (`/pools?dialog=join-pool&joinCode=<CODE>`) rather than resolving the code
 * itself: `JoinPoolDialog` already calls both `/pool/join-preview` and
 * `/pool/join`, and calling either again here would fire the request twice.
 *
 * Per Q220, nothing is browsable signed out: `requireSignedIn` runs first, so
 * a signed-out fan is bounced straight to sign-in with zero pool content ever
 * fetched or rendered here.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { normalizeJoinCode } from "../features/pools/joinPoolFlow";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";

const JoinLinkScreen = (): ReactNode => <RoutePendingScreen />;

export const Route = createFileRoute("/join/$code")({
  beforeLoad: (opts: {
    location: { href: string };
    params: { code: string };
  }): void => {
    requireSignedIn(opts);
    const code = normalizeJoinCode(opts.params.code);
    // oxlint-disable-next-line typescript/only-throw-error
    throw redirect({
      to: "/pools",
      search: {
        dialog: "join-pool",
        ...(code === "" ? {} : { joinCode: code }),
      },
      replace: true,
    });
  },
  component: JoinLinkScreen,
});
